import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { useMemo } from "react";

import { enterEmServiceMode, getEmMachines, leaveEmServiceMode } from "@/api/machines/em-api";
import { enterFusionServiceMode, getFusionMachines, leaveFusionServiceMode } from "@/api/machines/fusion-api";
import { getX5Machines } from "@/api/machines/x5-api";
import { type Machine, type X5Machine } from "@/schemas";

const QUERY_STALE_TIME = 2 * 1000;
const SERVICE_REFETCH_INTERVAL = 3 * 1000;
const CREATE_PRINT_SOLUTION_ROLE = "createprintsolution";
const FULFILLED_STATUS = "fulfilled";

const createFetchServiceMachines = (hasCreatePrintSolutionRole: boolean) => {
	return async (): Promise<Machine[] | null> => {
		try {
			const tasks = [getEmMachines(), getFusionMachines()];

			if (hasCreatePrintSolutionRole) {
				tasks.push(getX5Machines());
			}

			const results = await Promise.allSettled(tasks);

			const em = results[0];
			const fusion = results[1];
			const x5 = hasCreatePrintSolutionRole
				? results[2]
				: { status: "rejected" as const, reason: "User lacks createprintsolution role" };

			const machines: Machine[] = [];

			if (em.status === FULFILLED_STATUS && em.value) {
				machines.push(
					...em.value.map((machine) => ({
						...machine,
						machineType: "Em" as const,
					})),
				);
			}

			if (fusion.status === FULFILLED_STATUS && fusion.value) {
				machines.push(
					...fusion.value.map((machine) => ({
						...machine,
						machineType: "Fusion" as const,
					})),
				);
			}

			if (x5.status === FULFILLED_STATUS && x5.value) {
				(x5.value as X5Machine[]).forEach((machine) => {
					if (machine.settings?.hasEfiPrinter) {
						machines.push({
							...machine,
							machineType: "X5wPrint" as const,
							status: "Service",
						});
					}
				});
			}

			machines.sort((a, b) => (a.alias > b.alias ? 1 : -1));

			return machines;
		} catch {
			return null;
		}
	};
};

type UseServiceMachinesOptions = Omit<
	UseQueryOptions<Machine[] | null, Error, Machine[] | null, string[]>,
	"queryKey" | "queryFn"
> & {
	userRoles?: string[];
};

export const useServiceMachines = (options: UseServiceMachinesOptions = {}) => {
	const { userRoles = [], refetchInterval = SERVICE_REFETCH_INTERVAL, ...queryConfig } = options;

	const hasCreatePrintSolutionRole = useMemo(
		() => userRoles.some((role) => role.toLowerCase() === CREATE_PRINT_SOLUTION_ROLE),
		[userRoles],
	);

	const fetchServiceMachines = useMemo(
		() => createFetchServiceMachines(hasCreatePrintSolutionRole),
		[hasCreatePrintSolutionRole],
	);

	const query = useQuery({
		queryKey: ["service-machines", hasCreatePrintSolutionRole ? "with-x5wprint" : "without-x5wprint"],
		queryFn: fetchServiceMachines,
		refetchInterval,
		staleTime: QUERY_STALE_TIME,
		...queryConfig,
	});

	return {
		machines: query.data,
		isLoading: query.isLoading,
		error: query.error,
		refresh: query.refetch,
	};
};

export const useEnterServiceMode = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (machine: Machine): Promise<void> => {
			switch (machine.machineType) {
				case "Em":
					await enterEmServiceMode(machine.id);
					break;
				case "Fusion":
					await enterFusionServiceMode(machine.id);
					break;
				case "X5wPrint":
					break;
				default:
					throw new Error(`Enter service mode not supported for machine type: ${machine.machineType}`);
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["machines"] });
			queryClient.invalidateQueries({ queryKey: ["service-machines"] });
		},
	});
};

export const useLeaveServiceMode = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (machine: Machine): Promise<void> => {
			switch (machine.machineType) {
				case "Em":
					await leaveEmServiceMode(machine.id);
					break;
				case "Fusion":
					await leaveFusionServiceMode(machine.id);
					break;
				case "X5wPrint":
					break;
				default:
					throw new Error(`Leave service mode not supported for machine type: ${machine.machineType}`);
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["machines"] });
			queryClient.invalidateQueries({ queryKey: ["service-machines"] });
		},
	});
};
